// src/pages/TiktokLinks/TiktokLinks.tsx
import React from 'react';
import { track } from '@vercel/analytics';
import { CreatorUpdatesForm } from '../../components/CreatorUpdatesForm/CreatorUpdatesForm';
import {
  creatorHubLinks,
  featuredLinksVideo,
  flagshipCreatorLinks,
  secondaryCreatorLinks,
} from '../../data/creatorHub';
import { S } from './styles';
import { usePageMetadata } from '../../utils/usePageMetadata';

const linksHeroVideoWebmUrl = '/videos/home-bg-1280.webm';
const linksHeroVideoMp4Url = '/videos/home-bg-1280.mp4';
const linksHeroPosterUrl = '/images/home/bg-video-poster.jpg';

export const TiktokLinks: React.FC = () => {
  usePageMetadata({
    title: 'Links | Austin Davenport',
    description: 'Austin Davenport creator hub with the latest YouTube video, ClipboardIt, YouTool.io, all tools, Creator Updates, and support links.',
    path: '/links',
  });

  const trackLink = (id: string, title: string, destinationType: 'internal' | 'external') => {
    track('links_tile_clicked', {
      tile_id: id,
      tile_name: title,
      destination_type: destinationType,
      source_page: 'links',
    });
  };

  const visibleCreatorHubLinks = creatorHubLinks.filter(item => item.id !== 'creator-updates');
  const creatorUpdatesLink = creatorHubLinks.find(item => item.id === 'creator-updates');

  return (
    <S.Container>
      <S.ContentWrapper>
        <S.ProfileCard>
          <S.ProfileBackgroundVideo
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={linksHeroPosterUrl}
            aria-hidden="true"
          >
            <source src={linksHeroVideoWebmUrl} type="video/webm" />
            <source src={linksHeroVideoMp4Url} type="video/mp4" />
          </S.ProfileBackgroundVideo>

          <S.AvatarContainer>
            <S.Avatar>
              <img
                src="https://64.media.tumblr.com/44ab51b7b5c73d1a68f728d92becd3b3/029f5263603a04c1-96/s2048x3072/cd2e6fdd67cb4fe3ad30bf06ef4b75c42291e2f6.pnj"
                alt="Austin Davenport"
              />
            </S.Avatar>
            <S.StatusDot />
          </S.AvatarContainer>

          <S.ProfileInfo>
            <S.Username>Austin Davenport</S.Username>
            <S.JobTitle>Professional Video Editor</S.JobTitle>
          </S.ProfileInfo>
        </S.ProfileCard>

        <S.VideoFeature
          href={featuredLinksVideo.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${featuredLinksVideo.title}: ${featuredLinksVideo.description}`}
          onClick={() => trackLink('latest-youtube-video', featuredLinksVideo.title, 'external')}
        >
          <img
            src={featuredLinksVideo.thumbnail}
            alt=""
            loading="eager"
          />
          <S.VideoOverlay>
            <S.PlayButton aria-hidden="true">
              <i className="bx bx-play" />
            </S.PlayButton>
          </S.VideoOverlay>
        </S.VideoFeature>

        <S.FlagshipGrid aria-label="Flagship tools">
          {flagshipCreatorLinks.map(link => (
            <S.FlagshipTile
              key={link.id}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              $image={link.image}
              aria-label={link.description ? `${link.title}: ${link.description}` : link.title}
              onClick={() => trackLink(link.id, link.title, 'external')}
            >
              <S.TileShade />
              <S.FlagshipCopy>
                <h2>{link.title}</h2>
                {link.description && <p>{link.description}</p>}
              </S.FlagshipCopy>
              <S.ExternalIcon className="bx bx-link-external" aria-hidden="true" />
            </S.FlagshipTile>
          ))}
        </S.FlagshipGrid>

        <S.LinksGrid>
          {visibleCreatorHubLinks.map((item, index) => (
            <S.LinkCard
              key={item.id}
              href={item.url}
              target={item.external ? '_blank' : undefined}
              rel={item.external ? 'noopener noreferrer' : undefined}
              onClick={() => trackLink(item.id, item.title, item.external ? 'external' : 'internal')}
              style={{ '--index': index } as React.CSSProperties}
            >
              <S.CardGlow />
              <S.CardContent>
                <S.IconWrapper>
                  <i className={`bx ${item.icon}`} aria-hidden="true" />
                </S.IconWrapper>
                <S.TextContent>
                  <S.CardTitle>{item.title}</S.CardTitle>
                  <S.CardDescription>{item.description}</S.CardDescription>
                </S.TextContent>
                <S.ActionIcon>
                  <i className={item.external ? 'bx bx-link-external' : 'bx bx-chevron-right'} aria-hidden="true" />
                </S.ActionIcon>
              </S.CardContent>
            </S.LinkCard>
          ))}

          {creatorUpdatesLink && (
            <S.UpdatesSection style={{ '--index': visibleCreatorHubLinks.length } as React.CSSProperties}>
              <CreatorUpdatesForm source="source_links" showVisual />
            </S.UpdatesSection>
          )}
        </S.LinksGrid>

        <S.BottomSocials>
          {secondaryCreatorLinks.filter(link => link.id === 'youtube').map(link => (
            <S.SocialIconLink
              key={link.id}
              href={link.url}
              target={link.external ? '_blank' : undefined}
              rel={link.external ? 'noopener noreferrer' : undefined}
              aria-label={link.title}
              onClick={() => trackLink(link.id, link.title, link.external ? 'external' : 'internal')}
            >
              <i className={`bx ${link.icon}`} aria-hidden="true" />
            </S.SocialIconLink>
          ))}
        </S.BottomSocials>
      </S.ContentWrapper>
    </S.Container>
  );
};
